import { ElementMeasurement } from '../measurementsStore/elementMeasurements/elementMeasurementsTypes';

type PreviousMeasurementsById = Record<string, ElementMeasurement | undefined>;

type PendingBatchCollision = {
    elementIds: string[];
    previousMeasurements: PreviousMeasurementsById;
};

type CollisionDetectionManagerType = {
    enabled: boolean;
    temporarilyEnabled: boolean;
    blocked: boolean;
    logged: boolean;
    pendingBatchCollision: PendingBatchCollision | null;
    cancelPendingBatchCheck: (() => void) | null;
};

export const collisionDetectionManager: CollisionDetectionManagerType = {
    enabled: false,
    temporarilyEnabled: false,
    // Allows collision detection to be turned off in the rare scenario where it's
    // in an infinite loop
    blocked: false,
    logged: false,
    pendingBatchCollision: null,
    cancelPendingBatchCheck: null,
};

export const setPendingBatchCollision = (
    elementIds: string[] = [],
    previousMeasurements: PreviousMeasurementsById = {},
) => {
    collisionDetectionManager.pendingBatchCollision = {
        elementIds,
        previousMeasurements,
    };
};

export const clearPendingBatchCollision = () => {
    collisionDetectionManager.pendingBatchCollision = null;
};

export const isPendingBatchCollisionElementId = (elementId: string) =>
    collisionDetectionManager.pendingBatchCollision?.elementIds?.includes(elementId);

export const getPendingBatchCollision = () => collisionDetectionManager.pendingBatchCollision;

export const setPendingBatchCollisionCancelFn = (cancelFn: () => void) => {
    if (collisionDetectionManager.cancelPendingBatchCheck) {
        collisionDetectionManager.cancelPendingBatchCheck();
    }

    collisionDetectionManager.cancelPendingBatchCheck = cancelFn;
};

export const clearPendingBatchCollisionCancelFn = () => {
    collisionDetectionManager.cancelPendingBatchCheck = null;
};

export const disableCollisionDetection = () => {
    collisionDetectionManager.enabled = false;
    collisionDetectionManager.temporarilyEnabled = false;
};

export const enableCollisionDetection = (temporarilyEnabled = false) => {
    collisionDetectionManager.enabled = true;
    collisionDetectionManager.temporarilyEnabled = temporarilyEnabled;
};

/**
 * To prevent an infinite loop bug - this flag will prevent the actual collision moves
 * from being made, but will enable it to be logged until navigations resets the state
 */
export const blockCollisionDetectionUntilNavigation = () => {
    collisionDetectionManager.blocked = true;
    collisionDetectionManager.logged = false;
};

export const unblockCollisionDetectionAfterNavigation = () => {
    collisionDetectionManager.blocked = false;
    collisionDetectionManager.logged = false;
    clearPendingBatchCollision();

    if (collisionDetectionManager.cancelPendingBatchCheck) {
        collisionDetectionManager.cancelPendingBatchCheck();
    }

    clearPendingBatchCollisionCancelFn();
};

let timeoutId: NodeJS.Timeout | undefined;

/**
 * Enables collision detection for a single JavaScript event loop cycle,
 * unless it's already enabled, in which case it will simply leave it as enabled.
 */
export const enableCollisionDetectionTemporarily = () => {
    // If already enabled, then don't do so temporarily
    if (collisionDetectionManager.enabled && !collisionDetectionManager.temporarilyEnabled) return;

    // If not enabled, or temporarily enabled - enable collision detection, clear old timers and start a new timer
    enableCollisionDetection(true);

    clearTimeout(timeoutId);

    // Toggle off collision detection in two cycles
    // (Safari requires two for collapsing and expanding columns for some reason)
    timeoutId = setTimeout(() => {
        timeoutId = setTimeout(disableCollisionDetection, 0);
    }, 0);
};
